import { Heading, HeadingLevelContext, useHeadingLevel } from '@opengov/components-heading';
import { useContext } from 'react';
import { DATA_TEST } from './enums';
import { ResultContext, sizeStyles } from './ResultContext';
import type { ResultHeadingLevel, ResultTitleProps } from './types';

/** The Typography variant each `size` gives the title, as the `styleLevel` `Heading` takes. */
const STYLE_LEVEL: Partial<Record<string, ResultHeadingLevel>> = { h1: 1, h2: 2, h3: 3, h4: 4, h5: 5, h6: 6 };

const isHeadingLevel = (value: unknown): value is ResultHeadingLevel =>
  typeof value === 'number' && Number.isInteger(value) && value >= 1 && value <= 6;

/**
 * The heading level the title renders at.
 *
 * - An explicit `headingLevel` (1-6) wins.
 * - Inside a `HeadingLevel` from `@opengov/components-heading`, the Result sits under that level's heading, so its title
 *   is one level deeper, capped at 6. This is what wrapping the title in one more `HeadingLevel` would give.
 * - With no `HeadingLevel` above it, the title is an `<h2>`: L1 layouts render the page's `<h1>` from `title` without a
 *   `HeadingLevel`, so a Result in the page body sits directly under that `<h1>`.
 */
const resolveHeadingLevel = (headingLevel: unknown, outlineLevel: number): ResultHeadingLevel => {
  if (isHeadingLevel(headingLevel)) return headingLevel;
  if (outlineLevel >= 1) return Math.min(outlineLevel + 1, 6) as ResultHeadingLevel;
  return 2;
};

/**
 * Renders the title for the Result component, with responsive typography.
 *
 * The title is a `Heading`: its element comes from the page outline (see `resolveHeadingLevel`) and its look from
 * `size` and the viewport, through `styleLevel`. The element never follows `size` or the viewport, because a heading
 * level that did would skip levels in the page outline.
 */
export const ResultTitle = ({ children, headingLevel, dataTest, ...props }: ResultTitleProps) => {
  const context = useContext(ResultContext);
  const outlineLevel = useHeadingLevel();
  if (!context) throw new Error('Title must be used within an Result component');

  const { size, isMobile } = context;
  const { headerSize } = sizeStyles(size, isMobile);
  const level = resolveHeadingLevel(headingLevel, outlineLevel);

  return (
    <HeadingLevelContext.Provider value={level}>
      <Heading
        styleLevel={STYLE_LEVEL[String(headerSize)] ?? level}
        data-test={dataTest ?? DATA_TEST.result_title}
        {...props}
      >
        {children}
      </Heading>
    </HeadingLevelContext.Provider>
  );
};
